import { useCallback, useMemo, type CSSProperties, type ReactNode } from "react";
import {
  Badge,
  Button,
  Cluster,
  Diagnostic,
  EmptyState,
  Kicker,
  Note,
  Panel,
  Stack,
  Status,
  Table,
  Title,
} from "@fwf/fwfa-design-system";
import { applyFilters, toFilterable } from "../filters";
import type { FilterState } from "../filters";
import { titleCase } from "../format";
import type { Actions, AdminData, Toasts } from "../hooks";
import { gcpHealth, gcpState } from "../state";
import { healthTone, stateTone } from "../types";
import type { AdminGcpInstance, FilterableRow } from "../types";
import { SlugEditor } from "./SlugEditor";

export type SectionProps = {
  data: AdminData;
  filters: FilterState;
  actions: Actions;
  toasts: Toasts;
};

const INSTANCE_ACTIONS = ["start", "stop", "delete"] as const;
type InstanceAction = (typeof INSTANCE_ACTIONS)[number];

/** Muted second line inside a table cell: name, size, provider detail. */
const SUB_LINE = "fwfa-subtitle fwfa-subtitle--sm text-xs";

const ACTION_CLUSTER_STYLE: CSSProperties = {
  flexWrap: "nowrap",
  "--fwfa-cluster-gap": "var(--fwfa-space-2)",
} as CSSProperties;

function actionKey(resourceId: string, action: InstanceAction): string {
  return `gcp:${resourceId}:${action}`;
}

function projectInstance(row: AdminGcpInstance): FilterableRow {
  return toFilterable(gcpState(row), gcpHealth(row), [
    row.resourceId,
    row.ownerEmail,
    row.ownerUserId,
    ...row.projectNames,
    ...row.projectIds,
    ...(row.portSlugs ?? []),
  ]);
}

/**
 * The start and stop endpoints answer with the refreshed instance. Taking its state
 * closes the gap between the optimistic guess and what GCP actually did, without
 * waiting for the reload.
 */
function serverState(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const instance = (payload as { instance?: unknown }).instance;
  if (!instance || typeof instance !== "object") return null;
  const state = (instance as { state?: unknown }).state;
  return typeof state === "string" ? state : null;
}

function ownerLabel(row: AdminGcpInstance): string {
  return row.ownerEmail || row.ownerUserId || "—";
}

/** Names are what an operator recognises; ids are the fallback when a name is missing. */
function projectLabel(row: AdminGcpInstance): string {
  if (row.projectNames.length > 0) return row.projectNames.join(", ");
  if (row.projectIds.length > 0) return row.projectIds.join(", ");
  return "—";
}

function SectionHeader({ meta }: { meta?: ReactNode }) {
  return (
    <div className="fwfa-section-header">
      <div>
        <Kicker>Workspace provider</Kicker>
        <Title as="h2" className="fwfa-title--section">
          GCP Compute
        </Title>
      </div>
      {meta}
    </div>
  );
}

export function GcpSection({ data, filters, actions, toasts }: SectionProps) {
  const { gcp, setGcp } = data;
  const notice = data.notices.gcp;
  const instances = gcp?.instances;

  const patchInstance = useCallback(
    (resourceId: string, patch: Partial<AdminGcpInstance>) => {
      setGcp((prev) =>
        prev
          ? {
              ...prev,
              instances: prev.instances.map((row) =>
                row.resourceId === resourceId ? { ...row, ...patch } : row,
              ),
            }
          : prev,
      );
    },
    [setGcp],
  );

  const removeInstance = useCallback(
    (resourceId: string) => {
      setGcp((prev) =>
        prev
          ? { ...prev, instances: prev.instances.filter((row) => row.resourceId !== resourceId) }
          : prev,
      );
    },
    [setGcp],
  );

  const runInstanceAction = useCallback(
    (row: AdminGcpInstance, action: InstanceAction) => {
      // Delete has no interim state worth showing: labelling the row "Stopping"
      // while it is being torn down would be a lie if the call then fails. Today's
      // page dims the row instead, and this keeps that.
      const optimisticState =
        action === "start" ? "STAGING" : action === "stop" ? "STOPPING" : null;

      void actions.run({
        key: actionKey(row.resourceId, action),
        url: `/api/admin/gcp/instances/${action}`,
        body: { resourceId: row.resourceId, homeDiskName: row.homeDiskName || row.volume.name },
        confirm:
          action === "delete"
            ? {
                title: "Delete this instance?",
                body: `${row.resourceId} and its persistent home disk will be removed. This cannot be undone.`,
                confirmLabel: "Delete instance",
                danger: true,
              }
            : undefined,
        optimistic: optimisticState
          ? () => patchInstance(row.resourceId, { state: optimisticState })
          : undefined,
        revert: optimisticState
          ? () => patchInstance(row.resourceId, { state: row.state })
          : undefined,
        onSuccess: (payload) => {
          if (action === "delete") {
            removeInstance(row.resourceId);
            return;
          }
          const next = serverState(payload);
          if (next) patchInstance(row.resourceId, { state: next });
        },
        // Refetching straight after a delete races the provider: GCP still lists the
        // instance for a moment and the row reappears. Removing it here is the truth.
        reloadAfter: action !== "delete",
        successTitle:
          action === "start"
            ? "Instance started"
            : action === "stop"
              ? "Instance stopped"
              : "Instance deleted",
        successMessage: row.resourceId,
        failureTitle: `Failed to ${action}`,
      });
    },
    [actions, patchInstance, removeInstance],
  );

  const visible = useMemo(
    () => (instances ? applyFilters(instances, filters, projectInstance) : []),
    [instances, filters],
  );

  // Not configured and nothing to report: the section stays off the page entirely.
  if (!gcp) {
    if (!notice) return null;
    return (
      <Panel>
        <Stack>
          <SectionHeader />
          <Note variant="warn">{notice}</Note>
        </Stack>
      </Panel>
    );
  }

  if (!gcp.configured || !gcp.config) {
    return (
      <Panel>
        <Stack>
          <SectionHeader />
          <EmptyState title="GCP Compute is not configured">
            Set the project, zone, and instance template on the web service, then reload.
          </EmptyState>
        </Stack>
      </Panel>
    );
  }

  const config = gcp.config;
  const configRows: Array<[string, string]> = [
    ["Project", config.projectId],
    ["Zone", config.zone],
    ["Template", config.instanceTemplate],
    ["Home disk", `${config.homeDiskType} · ${config.homeDiskSizeGb} GB`],
    ["Home device", config.homeDiskDeviceName],
  ];

  return (
    <Panel>
      <Stack>
        <SectionHeader
          meta={
            <p className="fwfa-subtitle fwfa-subtitle--sm font-mono">
              {config.projectId} · {config.zone}
            </p>
          }
        />

        {/* One failed gcloud call can carry a paragraph of prose per instance. Folded
            away, the errors stay one click from an operator without owning the view. */}
        {gcp.errors.length > 0 ? (
          <details className="fwfa-diagnostic-details">
            <summary>
              {`GCP Compute reported ${gcp.errors.length} ${gcp.errors.length === 1 ? "error" : "errors"}`}
            </summary>
            <Diagnostic>{gcp.errors.join("\n")}</Diagnostic>
          </details>
        ) : null}

        {/* Reference data, not something anyone scans, so it starts closed. */}
        <details className="fwfa-diagnostic-details">
          <summary>Configuration</summary>
          <dl className="grid gap-fwfa-2 sm:grid-cols-2">
            {configRows.map(([label, value]) => (
              <div key={label} className="flex gap-fwfa-2">
                <dt className={SUB_LINE}>{label}</dt>
                <dd className="m-0 font-mono text-xs text-ink break-words">{value}</dd>
              </div>
            ))}
          </dl>
        </details>

        {visible.length === 0 ? (
          <EmptyState
            title={gcp.instances.length === 0 ? "No instances" : "No matching instances"}
          >
            {gcp.instances.length === 0
              ? "Nothing is provisioned in this project and zone yet."
              : "Every instance is filtered out. Clear the filters to see them all."}
          </EmptyState>
        ) : (
          <Table reflow>
            <thead>
              <tr>
                <th>Instance</th>
                <th>State</th>
                <th>Volume</th>
                <th>Owner</th>
                <th>Project</th>
                <th>Core</th>
                <th>Port slugs</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const pendingAction = INSTANCE_ACTIONS.find((action) =>
                  actions.isPending(actionKey(row.resourceId, action)),
                );
                const busy = pendingAction !== undefined;
                const state = gcpState(row);
                const health = gcpHealth(row);

                return (
                  <tr
                    key={row.resourceId}
                    className={pendingAction === "delete" ? "opacity-40" : undefined}
                  >
                    <td data-label="Instance">
                      <div>
                        <p className="font-mono text-sm text-ink break-words">{row.resourceId}</p>
                        <p className={SUB_LINE}>
                          {[row.zone, row.machineType, row.instanceTemplate]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                    </td>

                    <td data-label="State">
                      <Status tone={stateTone(state)} live={state === "running"}>
                        {titleCase(row.state)}
                      </Status>
                    </td>

                    <td data-label="Volume">
                      <div>
                        {row.volume.status === "attached" ? (
                          <Badge variant="soft">Attached</Badge>
                        ) : (
                          <Badge>{titleCase(row.volume.status)}</Badge>
                        )}
                        <p className={SUB_LINE}>
                          {row.volume.name}
                          {row.volume.sizeGb ? ` · ${row.volume.sizeGb} GB` : ""}
                        </p>
                        {row.volume.detail ? (
                          <p className={SUB_LINE}>{row.volume.detail}</p>
                        ) : null}
                      </div>
                    </td>

                    {/* Connected-user count rides with the owner rather than
                        taking its own column: nine columns squeezed the actions
                        into a vertical stack, and the count only ever qualifies
                        who is on the instance. */}
                    <td data-label="Owner" className="break-words">
                      <div>
                        {ownerLabel(row)}
                        <p className={SUB_LINE}>
                          {row.connectedUsers === 1 ? "1 connected" : `${row.connectedUsers} connected`}
                        </p>
                      </div>
                    </td>

                    <td data-label="Project" className="break-words">
                      {projectLabel(row)}
                    </td>

                    <td data-label="Core">
                      <div>
                        <Status tone={healthTone(health)}>{titleCase(health)}</Status>
                        {row.core.detail ? <p className={SUB_LINE}>{row.core.detail}</p> : null}
                      </div>
                    </td>

                    <td data-label="Port slugs">
                      <SlugEditor
                        resourceId={row.resourceId}
                        slugs={row.portSlugs ?? []}
                        disabled={busy}
                        onSlugsChanged={(slugs) => {
                          patchInstance(row.resourceId, { portSlugs: slugs });
                          toasts.success("Slugs updated", row.resourceId);
                        }}
                        toasts={toasts}
                      />
                    </td>

                    <td data-label="Actions">
                      {/* nowrap so the three buttons count as one min-content
                          width: left to wrap, the table starves this column and
                          stacks them vertically. */}
                      <Cluster style={ACTION_CLUSTER_STYLE}>
                        <Button
                          variant="secondary"
                          compact
                          loading={pendingAction === "start"}
                          disabled={busy || state === "running"}
                          onClick={() => runInstanceAction(row, "start")}
                        >
                          Start
                        </Button>
                        <Button
                          variant="secondary"
                          compact
                          loading={pendingAction === "stop"}
                          disabled={busy || state !== "running"}
                          onClick={() => runInstanceAction(row, "stop")}
                        >
                          Stop
                        </Button>
                        <Button
                          variant="secondary"
                          compact
                          loading={pendingAction === "delete"}
                          disabled={busy}
                          onClick={() => runInstanceAction(row, "delete")}
                        >
                          Delete
                        </Button>
                      </Cluster>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Stack>
    </Panel>
  );
}
